import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import AnalyticsDashboard from '../../components/AnalyticsDashboard';
import AdManagement from '../../components/AdManagement';
import SuspensionManager from '../../components/SuspensionManager';
import AdContractManager from '../../components/AdContractManager';
import CallTransferStats from '../../components/CallTransferStats';
import AdBanner from '../../components/AdBanner';
import { 
  Globe, 
  Users, 
  Shield, 
  BarChart3, 
  Bell,
  Plus,
  Settings,
  Loader2,
  Search,
  RefreshCw,
  Key,
  Megaphone,
  TrendingUp,
  DollarSign,
  Eye,
  Copy,
  Trash2,
  FileSignature,
  Clock,
  Power,
  PieChart,
  Gift,
  X,
  Phone,
  PhoneForwarded,
  AlertTriangle,
  ShieldOff,
  Ban,
  UserX,
  AlertCircle,
  CheckCircle,
  XCircle,
  Zap,
  Send,
  MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Assistant Selector Component
const AssistantSelector = ({ token }) => {
  const [settings, setSettings] = useState(null);
  const [switching, setSwitching] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API_URL}/api/settings/voice-assistant`, { headers })
      .then(res => setSettings(res.data))
      .catch(() => {});
  }, []);

  const switchAssistant = async (key) => {
    setSwitching(true);
    try {
      const res = await axios.patch(`${API_URL}/api/settings/voice-assistant`, { active_assistant: key }, { headers });
      setSettings(prev => ({ ...prev, active_assistant: key, assistant_id: res.data.assistant_id }));
      toast.success(`Assistant basculé vers ${key === 'elevenlabs' ? 'ElevenLabs' : 'Azure/Vibe Voice'}`);
    } catch (err) {
      toast.error('Erreur lors du changement');
    }
    setSwitching(false);
  };

  if (!settings) return <Loader2 className="w-5 h-5 animate-spin" />;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(settings.presets || {}).map(([key, preset]) => (
          <div
            key={key}
            className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
              settings.active_assistant === key
                ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                : 'border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => !switching && switchAssistant(key)}
            data-testid={`assistant-option-${key}`}
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-slate-900">{preset.name}</h4>
              {settings.active_assistant === key && (
                <Badge className="bg-indigo-600">Actif</Badge>
              )}
            </div>
            <p className="text-sm text-slate-500 mb-2">Voix : {preset.voice}</p>
            <div className="flex flex-wrap gap-1 mb-2">
              {preset.features?.map((f, i) => (
                <Badge key={i} variant="outline" className="text-xs">{f}</Badge>
              ))}
            </div>
            <p className="text-xs text-slate-400">{preset.note}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400">
        ID actif : <code className="bg-slate-100 px-1 rounded">{settings.assistant_id}</code>
      </p>
    </div>
  );
};

// Automation Panel Component
const AutomationPanel = ({ token }) => {
  const [reengageStats, setReengageStats] = useState(null);
  const [reengagePreview, setReengagePreview] = useState(null);
  const [socialPosts, setSocialPosts] = useState([]);
  const [newPost, setNewPost] = useState({ content: '', platform: 'all', language: 'fr' });
  const [running, setRunning] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchAutomationData();
  }, []);

  const fetchAutomationData = async () => {
    try {
      const [statsRes, postsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/automation/reengagement/stats`, { headers }),
        axios.get(`${API_URL}/api/admin/automation/social-posts`, { headers })
      ]);
      setReengageStats(statsRes.data);
      setSocialPosts(postsRes.data.posts || []);
    } catch (err) { console.error(err); }
  };

  const previewReengagement = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/automation/reengagement/preview`, { headers });
      setReengagePreview(res.data);
    } catch (err) { toast.error('Erreur'); }
  };

  const runReengagement = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/automation/reengagement/run`, {}, { headers });
      toast.success(`${res.data.sent} emails envoyés`);
      fetchAutomationData();
    } catch (err) { toast.error('Erreur'); }
    setRunning(false);
  };

  const createPost = async () => {
    if (!newPost.content.trim()) return;
    try {
      await axios.post(`${API_URL}/api/admin/automation/social-posts`, newPost, { headers });
      setNewPost({ content: '', platform: 'all', language: 'fr' });
      fetchAutomationData();
      toast.success('Brouillon créé');
    } catch (err) { toast.error('Erreur'); }
  };

  const updatePostStatus = async (postId, status) => {
    try {
      await axios.patch(`${API_URL}/api/admin/automation/social-posts/${postId}`, { status }, { headers });
      fetchAutomationData();
      toast.success(status === 'approved' ? 'Post approuvé' : 'Statut mis à jour');
    } catch (err) { toast.error('Erreur'); }
  };

  const deletePost = async (postId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/automation/social-posts/${postId}`, { headers });
      fetchAutomationData();
    } catch (err) { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-6">
      {/* Re-engagement Section */}
      <Card className="border-2 border-orange-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-orange-600" />
            Relance Patients Inactifs
          </CardTitle>
          <CardDescription>
            Email automatique aux patients sans RDV depuis 30+ jours (RGPD : opt-out respecté)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {reengageStats && (
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <p className="text-2xl font-bold text-orange-700">{reengageStats.total_sent}</p>
                <p className="text-xs text-slate-500">Total envoyés</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-700">{reengageStats.last_7_days}</p>
                <p className="text-xs text-slate-500">7 derniers jours</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-700">{reengageStats.last_30_days}</p>
                <p className="text-xs text-slate-500">30 derniers jours</p>
              </div>
            </div>
          )}
          <div className="flex gap-3">
            <Button variant="outline" onClick={previewReengagement} data-testid="reengage-preview">
              Prévisualiser les patients
            </Button>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={runReengagement} disabled={running} data-testid="reengage-run">
              {running ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Lancer la relance
            </Button>
          </div>
          {reengagePreview && (
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="font-medium mb-2">{reengagePreview.count} patients inactifs identifiés :</p>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {reengagePreview.patients?.map((p, i) => (
                  <p key={i} className="text-sm text-slate-600">{p.name || 'Sans nom'} — {p.email}</p>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Social Media Posts Section */}
      <Card className="border-2 border-purple-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-purple-600" />
            Posts Réseaux Sociaux
          </CardTitle>
          <CardDescription>
            Créez, prévisualisez et approuvez les posts avant publication
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* New Post Form */}
          <div className="space-y-3 p-4 bg-purple-50/50 rounded-lg">
            <textarea
              className="w-full p-3 border rounded-lg resize-none h-24 text-sm"
              placeholder="Rédigez votre post ici..."
              value={newPost.content}
              onChange={e => setNewPost({ ...newPost, content: e.target.value })}
              data-testid="social-post-content"
            />
            <div className="flex gap-3 items-center">
              <select
                className="border rounded-lg px-3 py-2 text-sm"
                value={newPost.platform}
                onChange={e => setNewPost({ ...newPost, platform: e.target.value })}
              >
                <option value="all">Toutes les plateformes</option>
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
                <option value="tiktok">TikTok</option>
                <option value="linkedin">LinkedIn</option>
              </select>
              <select
                className="border rounded-lg px-3 py-2 text-sm"
                value={newPost.language}
                onChange={e => setNewPost({ ...newPost, language: e.target.value })}
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="de">Deutsch</option>
                <option value="es">Español</option>
                <option value="it">Italiano</option>
                <option value="pt">Português</option>
                <option value="ar">العربية</option>
                <option value="bg">Български</option>
              </select>
              <Button onClick={createPost} className="bg-purple-600 hover:bg-purple-700" data-testid="create-social-post">
                Créer le brouillon
              </Button>
            </div>
          </div>

          {/* Posts List */}
          {socialPosts.length > 0 ? (
            <div className="space-y-3">
              {socialPosts.map(post => (
                <div key={post.id} className={`p-4 rounded-lg border ${
                  post.status === 'approved' ? 'bg-green-50 border-green-200' :
                  post.status === 'draft' ? 'bg-white border-slate-200' :
                  'bg-slate-50'
                }`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant={post.status === 'approved' ? 'default' : 'outline'}>
                        {post.status === 'draft' ? 'Brouillon' : post.status === 'approved' ? 'Approuvé' : post.status}
                      </Badge>
                      <Badge variant="outline" className="text-xs">{post.platform}</Badge>
                      <span className="text-xs text-slate-400">{post.language?.toUpperCase()}</span>
                    </div>
                    <div className="flex gap-1">
                      {post.status === 'draft' && (
                        <Button size="sm" variant="outline" className="text-green-600" onClick={() => updatePostStatus(post.id, 'approved')}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Approuver
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deletePost(post.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{post.content}</p>
                  <p className="text-xs text-slate-400 mt-2">{new Date(post.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-400 py-6">Aucun post créé</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const SuperAdminDashboard = () => {
  const { t, language, isRTL } = useLanguage();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showAddCountry, setShowAddCountry] = useState(false);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // New state for beta codes, marketing, and stats
  const [betaCodes, setBetaCodes] = useState([]);
  const [marketingSettings, setMarketingSettings] = useState(null);
  const [connectionStats, setConnectionStats] = useState(null);
  const [revenueStats, setRevenueStats] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [showCreateContract, setShowCreateContract] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState('');
  
  // Beta mode ON/OFF
  const [betaModeEnabled, setBetaModeEnabled] = useState(true);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [betaAnalytics, setBetaAnalytics] = useState(null);

  // Deputy Admins, Maintenance, Pricing
  const [deputies, setDeputies] = useState([]);
  const [showAddDeputy, setShowAddDeputy] = useState(false);
  const [maintenance, setMaintenance] = useState({ global: null, per_country: [] });
  const [pricing, setPricing] = useState(null);
  const [showEditPricing, setShowEditPricing] = useState(false);
  const [suspendedUsers, setSuspendedUsers] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);

  // Trial Codes State
  const [trialCodes, setTrialCodes] = useState([]);
  const [creatingTrialCode, setCreatingTrialCode] = useState(false);
  const [trialCodeNote, setTrialCodeNote] = useState('');

  // Telephony / Call Forwarding State
  const [callForwardingConfig, setCallForwardingConfig] = useState({
    french_mobile: '+33622853762',
    us_vapi_number: '+12138452813',
    is_active: false
  });
  const [savingCallForwarding, setSavingCallForwarding] = useState(false);

  // Security Dashboard State
  const [securityDashboard, setSecurityDashboard] = useState(null);
  const [overduePayments, setOverduePayments] = useState([]);
  const [activeShutdowns, setActiveShutdowns] = useState([]);
  const [showEmergencyShutdown, setShowEmergencyShutdown] = useState(false);
  const [shutdownReason, setShutdownReason] = useState('');
  const [shutdownScope, setShutdownScope] = useState('global');
  const [shutdownCountry, setShutdownCountry] = useState('');
  const [processingShutdown, setProcessingShutdown] = useState(false);

  // Friend Codes State
  const [friendCodes, setFriendCodes] = useState([]);
  const [friendCodeStats, setFriendCodeStats] = useState(null);
  const [newCodeRecipient, setNewCodeRecipient] = useState('');
  const [newCodeNote, setNewCodeNote] = useState('');
  const [newCodeDuration, setNewCodeDuration] = useState(15);

  // Signups State
  const [recentSignups, setRecentSignups] = useState({ professionals: [], patients: [] });
  const [signupsPeriod, setSignupsPeriod] = useState(30);

  const [newDeputy, setNewDeputy] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    rights: {
      manage_patients: false,
      manage_professionals: false,
      manage_countries: false,
      view_statistics: false,
      maintenance_mode: false,
      manage_payments: false,
      manage_contracts: false,
      send_notifications: false
    }
  });

  const [newCountry, setNewCountry] = useState({
    code: '',
    name_fr: '',
    name_en: '',
    name_de: '',
    name_es: '',
    name_pt: '',
    name_ar: '',
    name_it: ''
  });

  const [newAdmin, setNewAdmin] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    language: 'fr',
    country_code: ''
  });

  const [adminRights, setAdminRights] = useState({
    can_manage_professionals: false,
    can_manage_patients: false,
    can_view_appointments: false,
    can_manage_appointments: false,
    can_view_statistics: false,
    can_send_notifications: false
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [countriesRes, adminsRes, statsRes, notifRes, betaRes, marketingRes, connectionRes, revenueRes, contractsRes, prosRes, settingsRes, betaAnalyticsRes, deputiesRes, maintenanceRes, pricingRes, trialCodesRes] = await Promise.all([
        axios.get(`${API_URL}/api/countries`, { headers }),
        axios.get(`${API_URL}/api/admins`, { headers }),
        axios.get(`${API_URL}/api/statistics`, { headers }),
        axios.get(`${API_URL}/api/notifications/logs`, { headers }),
        axios.get(`${API_URL}/api/beta-codes`, { headers }),
        axios.get(`${API_URL}/api/marketing/settings`, { headers }),
        axios.get(`${API_URL}/api/statistics/connections`, { headers }),
        axios.get(`${API_URL}/api/admin/statistics/revenue`, { headers }),
        axios.get(`${API_URL}/api/contracts`, { headers }),
        axios.get(`${API_URL}/api/professionals`, { headers }),
        axios.get(`${API_URL}/api/settings/app`, { headers }),
        axios.get(`${API_URL}/api/beta-codes/analytics`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/admin/deputies`, { headers }).catch(() => ({ data: { deputies: [] } })),
        axios.get(`${API_URL}/api/maintenance`, { headers }).catch(() => ({ data: { global: null, per_country: [] } })),
        axios.get(`${API_URL}/api/pricing`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/admin/trial-codes`, { headers }).catch(() => ({ data: { codes: [] } }))
      ]);
      setCountries(countriesRes.data);
      setAdmins(adminsRes.data);
      setStatistics(statsRes.data);
      setNotifications(notifRes.data.slice(0, 20));
      setBetaCodes(betaRes.data);
      setMarketingSettings(marketingRes.data);
      setConnectionStats(connectionRes.data);
      setRevenueStats(revenueRes.data);
      setContracts(contractsRes.data?.contracts || contractsRes.data || []);
      setProfessionals(prosRes.data);
      setBetaModeEnabled(settingsRes.data?.beta_mode ?? true);
      setBetaAnalytics(betaAnalyticsRes.data);
      setDeputies(deputiesRes.data?.deputies || []);
      setMaintenance(maintenanceRes.data);
      setPricing(pricingRes.data);
      setTrialCodes(trialCodesRes.data?.codes || []);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchSecurityData();
    fetchFriendCodes();
    fetchRecentSignups();
  }, [token]);

  // Fetch Recent Signups
  const fetchRecentSignups = async (days = signupsPeriod) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/signups/recent?days=${days}`, { headers });
      setRecentSignups(res.data || { professionals: [], patients: [] });
    } catch (error) {
      console.error('Signups fetch error:', error);
    }
  };

  // Fetch Friend Codes
  const fetchFriendCodes = async () => {
    try {
      const [codesRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/friend-codes/list`, { headers }).catch(() => ({ data: { codes: [] } })),
        axios.get(`${API_URL}/api/friend-codes/statistics`, { headers }).catch(() => ({ data: null }))
      ]);
      setFriendCodes(codesRes.data?.codes || []);
      setFriendCodeStats(statsRes.data);
    } catch (error) {
      console.error('Friend codes fetch error:', error);
    }
  };

  const generateFriendCode = async (type) => {
    setGeneratingCode(true);
    try {
      const endpoint = type === 'professional' ? 'generate/professional' : 'generate/patient';
      const response = await axios.post(
        `${API_URL}/api/friend-codes/${endpoint}`,
        { recipient_name: newCodeRecipient, note: newCodeNote, duration_days: newCodeDuration },
        { headers }
      );
      toast.success(`Code généré : ${response.data.code} (${newCodeDuration} jours)`);
      setNewCodeRecipient('');
      setNewCodeNote('');
      fetchFriendCodes();
    } catch (error) {
      toast.error('Erreur lors de la génération');
    } finally {
      setGeneratingCode(false);
    }
  };

  // Fetch Security Dashboard Data
  const fetchSecurityData = async () => {
    try {
      const [dashboardRes, overdueRes, shutdownsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/security/dashboard`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/admin/security/overdue-payments`, { headers }).catch(() => ({ data: { overdue_users: [] } })),
        axios.get(`${API_URL}/api/admin/security/active-shutdowns`, { headers }).catch(() => ({ data: { shutdowns: [] } }))
      ]);
      setSecurityDashboard(dashboardRes.data);
      setOverduePayments(overdueRes.data?.overdue_users || []);
      setActiveShutdowns(shutdownsRes.data?.shutdowns || []);
    } catch (error) {
      console.error('Security fetch error:', error);
    }
  };

  const toggleCountryStatus = async (countryCode, currentStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/countries/${countryCode}`,
        { is_active: !currentStatus },
        { headers }
      );
      setCountries(countries.map(c => 
        c.code === countryCode ? { ...c, is_active: !currentStatus } : c
      ));
      toast.success(t('success'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleAddCountry = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/countries`, newCountry, { headers });
      setCountries([...countries, response.data]);
      setShowAddCountry(false);
      setNewCountry({ code: '', name_fr: '', name_en: '', name_de: '', name_es: '', name_pt: '', name_ar: '', name_it: '' });
      toast.success(t('success'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleAddAdmin = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/admins/create-country-admin?country_code=${newAdmin.country_code}`,
        newAdmin,
        { headers }
      );
      setAdmins([...admins, response.data]);
      setShowAddAdmin(false);
      setNewAdmin({ email: '', password: '', first_name: '', last_name: '', phone: '', language: 'fr', country_code: '' });
      toast.success(t('success'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleUpdateAdminRights = async () => {
    if (!selectedAdmin) return;
    try {
      await axios.patch(
        `${API_URL}/api/admins/${selectedAdmin.id}/rights`,
        adminRights,
        { headers }
      );
      toast.success(t('success'));
      setSelectedAdmin(null);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const openRightsDialog = async (admin) => {
    setSelectedAdmin(admin);
    try {
      const response = await axios.get(`${API_URL}/api/admins/${admin.id}/rights`, { headers });
      setAdminRights(response.data.rights || {
        can_manage_professionals: false,
        can_manage_patients: false,
        can_view_appointments: false,
        can_manage_appointments: false,
        can_view_statistics: false,
        can_send_notifications: false
      });
    } catch (error) {
      console.error('Fetch rights error:', error);
    }
  };

  // Beta Code Functions
  const handleGenerateBetaCode = async (durationType) => {
    setGeneratingCode(true);
    try {
      const response = await axios.post(`${API_URL}/api/beta-codes/generate`, {
        duration_type: durationType,
        max_uses: 1,
        note: `Code ${durationType}`
      }, { headers });
      setBetaCodes([response.data, ...betaCodes]);
      toast.success(`Code ${durationType} généré: ${response.data.code}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleToggleBetaMode = async () => {
    try {
      const newValue = !betaModeEnabled;
      await axios.patch(`${API_URL}/api/settings/app`, { beta_mode: newValue }, { headers });
      setBetaModeEnabled(newValue);
      toast.success(newValue ? 'Mode Beta activé - Code requis' : 'Mode Beta désactivé - Accès libre');
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleToggleBetaCode = async (codeId) => {
    try {
      await axios.patch(`${API_URL}/api/beta-codes/${codeId}/toggle`, {}, { headers });
      setBetaCodes(betaCodes.map(c => 
        c.id === codeId ? { ...c, is_active: !c.is_active } : c
      ));
      toast.success(t('success'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleDeleteBetaCode = async (codeId) => {
    try {
      await axios.delete(`${API_URL}/api/beta-codes/${codeId}`, { headers });
      setBetaCodes(betaCodes.filter(c => c.id !== codeId));
      toast.success(t('success'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Code copié!');
  };

  // Marketing Functions
  const handleToggleMarketing = async (platform) => {
    const newSettings = { ...marketingSettings, [`${platform}_enabled`]: !marketingSettings[`${platform}_enabled`] };
    try {
      await axios.patch(`${API_URL}/api/marketing/settings`, newSettings, { headers });
      setMarketingSettings(newSettings);
      toast.success(t('success'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  // Contract Functions
  const handleCreateContract = async () => {
    if (!selectedProfessional) return;
    try {
      const response = await axios.post(`${API_URL}/api/contracts/generate`, {
        professional_id: selectedProfessional,
        duration_months: 12,
        monthly_fee: pricing?.professional_contract || 150
      }, { headers });
      setContracts([...contracts, response.data]);
      setShowCreateContract(false);
      setSelectedProfessional('');
      toast.success('Contrat créé et envoyé pour signature!');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  // Deputy Admin Functions
  const handleAddDeputy = async () => {
    if (!newDeputy.email || !newDeputy.password) {
      toast.error('Email et mot de passe requis');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/deputies`, newDeputy, { headers });
      toast.success('Admin suppléant créé');
      setShowAddDeputy(false);
      setNewDeputy({
        email: '', password: '', first_name: '', last_name: '',
        rights: { manage_patients: false, manage_professionals: false, manage_countries: false, view_statistics: false, maintenance_mode: false, manage_payments: false, manage_contracts: false, send_notifications: false }
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleDeleteDeputy = async (deputyId) => {
    if (!window.confirm('Supprimer cet admin suppléant ?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/deputies/${deputyId}`, { headers });
      setDeputies(deputies.filter(d => d.id !== deputyId));
      toast.success('Admin supprimé');
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleUpdateDeputyRights = async (deputyId, rights) => {
    try {
      await axios.patch(`${API_URL}/api/admin/deputies/${deputyId}/rights`, rights, { headers });
      setDeputies(deputies.map(d => d.id === deputyId ? { ...d, admin_rights: rights } : d));
      toast.success('Droits mis à jour');
    } catch (error) {
      toast.error(t('error'));
    }
  };

  // Maintenance Functions
  const handleSetMaintenance = async (enabled, countryCode = null) => {
    try {
      await axios.post(`${API_URL}/api/maintenance`, {
        enabled,
        country_code: countryCode,
        message: enabled ? 'Maintenance en cours. Merci de votre patience.' : null
      }, { headers });
      toast.success(enabled ? 'Maintenance activée' : 'Maintenance désactivée');
      fetchData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  // Pricing Functions
  const handleUpdatePricing = async (field, value) => {
    try {
      await axios.patch(`${API_URL}/api/pricing`, { [field]: parseFloat(value) }, { headers });
      setPricing({ ...pricing, [field]: parseFloat(value) });
      toast.success('Prix mis à jour');
    } catch (error) {
      toast.error(t('error'));
    }
  };

  // Trial Codes Management
  const handleCreateTrialCode = async () => {
    setCreatingTrialCode(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/trial-codes`, {
        duration_days: 14,
        max_uses: 1,
        note: trialCodeNote
      }, { headers });
      setTrialCodes([response.data, ...trialCodes]);
      setTrialCodeNote('');
      toast.success(`Code d'essai généré: ${response.data.code}`);
      copyToClipboard(response.data.code);
    } catch (error) {
      toast.error('Erreur lors de la création du code');
    } finally {
      setCreatingTrialCode(false);
    }
  };

  const handleExtendTrialCode = async (codeId, days) => {
    try {
      await axios.patch(`${API_URL}/api/admin/trial-codes/${codeId}/extend?days=${days}`, {}, { headers });
      setTrialCodes(trialCodes.map(c => 
        c.id === codeId ? { ...c, duration_days: c.duration_days + days } : c
      ));
      toast.success(`Prolongé de ${days} jours`);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeactivateTrialCode = async (codeId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/trial-codes/${codeId}`, { headers });
      setTrialCodes(trialCodes.map(c => 
        c.id === codeId ? { ...c, is_active: false } : c
      ));
      toast.success('Code désactivé');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // System Health
  const handleSystemRepair = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/system/repair`, {}, { headers });
      toast.success('Réparation effectuée');
      setSystemHealth(response.data);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const filteredCountries = countries.filter(c => 
    c.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.name_en.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="super-admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className={`flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Super Admin Dashboard</h1>
            <p className="text-slate-500 mt-1">{t('admin_countries')} & {t('admin_admins')}</p>
          </div>
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card data-testid="stat-users">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{t('admin_users')}</p>
                  <p className="text-2xl font-bold text-slate-900">{statistics?.total_users || 0}</p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-professionals">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{t('nav_professionals')}</p>
                  <p className="text-2xl font-bold text-slate-900">{statistics?.total_professionals || 0}</p>
                </div>
                <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-secondary" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-appointments">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{t('dashboard_appointments')}</p>
                  <p className="text-2xl font-bold text-slate-900">{statistics?.total_appointments || 0}</p>
                </div>
                <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-accent" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-waitlist">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{t('dashboard_waitlist')}</p>
                  <p className="text-2xl font-bold text-slate-900">{statistics?.active_waitlist || 0}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Bell className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="countries">
          <TabsList className="mb-6 flex-wrap gap-1">
            {/* 🔵 GESTION - Bleu */}
            <TabsTrigger value="countries" className="gap-2 bg-blue-50 text-blue-700 data-[state=active]:bg-blue-200 data-[state=active]:text-blue-900" data-testid="tab-countries">
              <Globe className="w-4 h-4" />
              {t('admin_countries')}
            </TabsTrigger>
            <TabsTrigger value="admins" className="gap-2 bg-blue-50 text-blue-700 data-[state=active]:bg-blue-200 data-[state=active]:text-blue-900" data-testid="tab-admins">
              <Users className="w-4 h-4" />
              {t('admin_admins')}
            </TabsTrigger>
            <TabsTrigger value="signups" className="gap-2 bg-blue-50 text-blue-700 data-[state=active]:bg-blue-200 data-[state=active]:text-blue-900" data-testid="tab-signups">
              <Plus className="w-4 h-4" />
              Inscriptions
            </TabsTrigger>
            <TabsTrigger value="deputies" className="gap-2 bg-blue-50 text-blue-700 data-[state=active]:bg-blue-200 data-[state=active]:text-blue-900" data-testid="tab-deputies">
              <Shield className="w-4 h-4" />
              Suppléants
            </TabsTrigger>
            
            {/* 🟢 ACCÈS & CODES - Vert */}
            <TabsTrigger value="beta-codes" className="gap-2 bg-emerald-50 text-emerald-700 data-[state=active]:bg-emerald-200 data-[state=active]:text-emerald-900" data-testid="tab-beta-codes">
              <Key className="w-4 h-4" />
              Codes d'accès
            </TabsTrigger>
            <TabsTrigger value="friend-codes" className="gap-2 bg-emerald-50 text-emerald-700 data-[state=active]:bg-emerald-200 data-[state=active]:text-emerald-900" data-testid="tab-friend-codes">
              <Gift className="w-4 h-4" />
              Codes Amis
            </TabsTrigger>
            
            {/* 🟡 FINANCE - Jaune/Ambre */}
            <TabsTrigger value="pricing" className="gap-2 bg-amber-50 text-amber-700 data-[state=active]:bg-amber-200 data-[state=active]:text-amber-900" data-testid="tab-pricing">
              <DollarSign className="w-4 h-4" />
              Prix
            </TabsTrigger>
            <TabsTrigger value="contracts" className="gap-2 bg-amber-50 text-amber-700 data-[state=active]:bg-amber-200 data-[state=active]:text-amber-900" data-testid="tab-contracts">
              <FileSignature className="w-4 h-4" />
              Contrats Pro
            </TabsTrigger>
            <TabsTrigger value="statistics" className="gap-2 bg-amber-50 text-amber-700 data-[state=active]:bg-amber-200 data-[state=active]:text-amber-900" data-testid="tab-statistics">
              <TrendingUp className="w-4 h-4" />
              Statistiques
            </TabsTrigger>
            
            {/* 🟣 MARKETING & PUB - Violet */}
            <TabsTrigger value="marketing" className="gap-2 bg-purple-50 text-purple-700 data-[state=active]:bg-purple-200 data-[state=active]:text-purple-900" data-testid="tab-marketing">
              <Megaphone className="w-4 h-4" />
              Campagnes
            </TabsTrigger>
            <TabsTrigger value="ads" className="gap-2 bg-purple-50 text-purple-700 data-[state=active]:bg-purple-200 data-[state=active]:text-purple-900" data-testid="tab-ads">
              <Megaphone className="w-4 h-4" />
              Publicités
            </TabsTrigger>
            <TabsTrigger value="ad-contracts" className="gap-2 bg-purple-50 text-purple-700 data-[state=active]:bg-purple-200 data-[state=active]:text-purple-900" data-testid="tab-ad-contracts">
              <FileSignature className="w-4 h-4" />
              Contrats Pub
            </TabsTrigger>
            
            {/* 🔴 SÉCURITÉ & MAINTENANCE - Rouge */}
            <TabsTrigger value="security" className="gap-2 bg-red-50 text-red-700 data-[state=active]:bg-red-200 data-[state=active]:text-red-900" data-testid="tab-security">
              <Shield className="w-4 h-4" />
              Sécurité
            </TabsTrigger>
            <TabsTrigger value="maintenance" className="gap-2 bg-red-50 text-red-700 data-[state=active]:bg-red-200 data-[state=active]:text-red-900" data-testid="tab-maintenance">
              <Power className="w-4 h-4" />
              Maintenance
            </TabsTrigger>
            <TabsTrigger value="suspension" className="gap-2 bg-red-50 text-red-700 data-[state=active]:bg-red-200 data-[state=active]:text-red-900" data-testid="tab-suspension">
              <UserX className="w-4 h-4" />
              Suspensions
            </TabsTrigger>
            
            {/* ⚪ COMMUNICATION - Gris/Slate */}
            <TabsTrigger value="notifications" className="gap-2 bg-slate-100 text-slate-700 data-[state=active]:bg-slate-300 data-[state=active]:text-slate-900" data-testid="tab-notifications">
              <Bell className="w-4 h-4" />
              {t('admin_notifications')}
            </TabsTrigger>
            <TabsTrigger value="telephony" className="gap-2 bg-slate-100 text-slate-700 data-[state=active]:bg-slate-300 data-[state=active]:text-slate-900" data-testid="tab-telephony">
              <Phone className="w-4 h-4" />
              Téléphonie
            </TabsTrigger>
            <TabsTrigger value="automation" className="gap-2 bg-orange-50 text-orange-700 data-[state=active]:bg-orange-200 data-[state=active]:text-orange-900" data-testid="tab-automation">
              <Zap className="w-4 h-4" />
              Automatisations
            </TabsTrigger>
            <TabsTrigger value="analytics" className="gap-2 bg-slate-100 text-slate-700 data-[state=active]:bg-slate-300 data-[state=active]:text-slate-900" data-testid="tab-analytics">
              <PieChart className="w-4 h-4" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Countries Tab */}
          <TabsContent value="countries">
            <Card>
              <CardHeader className={`flex flex-row items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div>
                  <CardTitle>{t('admin_countries')}</CardTitle>
                  <CardDescription>Activez ou désactivez les pays disponibles</CardDescription>
                </div>
                <Dialog open={showAddCountry} onOpenChange={setShowAddCountry}>
                  <DialogTrigger asChild>
                    <Button className="gap-2" data-testid="add-country-btn">
                      <Plus className="w-4 h-4" />
                      {t('admin_add_country')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>{t('admin_add_country')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Code (ISO)</Label>
                        <Input 
                          value={newCountry.code} 
                          onChange={(e) => setNewCountry({ ...newCountry, code: e.target.value.toUpperCase() })}
                          placeholder="FR, DE, ES..."
                          maxLength={2}
                          data-testid="country-code-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Nom (FR)</Label>
                          <Input 
                            value={newCountry.name_fr} 
                            onChange={(e) => setNewCountry({ ...newCountry, name_fr: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Name (EN)</Label>
                          <Input 
                            value={newCountry.name_en} 
                            onChange={(e) => setNewCountry({ ...newCountry, name_en: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Name (DE)</Label>
                          <Input 
                            value={newCountry.name_de} 
                            onChange={(e) => setNewCountry({ ...newCountry, name_de: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Nombre (ES)</Label>
                          <Input 
                            value={newCountry.name_es} 
                            onChange={(e) => setNewCountry({ ...newCountry, name_es: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Nome (PT)</Label>
                          <Input 
                            value={newCountry.name_pt} 
                            onChange={(e) => setNewCountry({ ...newCountry, name_pt: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>الاسم (AR)</Label>
                          <Input 
                            value={newCountry.name_ar} 
                            onChange={(e) => setNewCountry({ ...newCountry, name_ar: e.target.value })}
                            dir="rtl"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Nome (IT)</Label>
                          <Input 
                            value={newCountry.name_it} 
                            onChange={(e) => setNewCountry({ ...newCountry, name_it: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAddCountry(false)}>
                        {t('cancel')}
                      </Button>
                      <Button onClick={handleAddCountry} data-testid="confirm-add-country">
                        {t('save')}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="relative">
                    <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                    <Input 
                      placeholder={t('search')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={isRTL ? 'pr-10' : 'pl-10'}
                      data-testid="country-search"
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  {filteredCountries.map((country) => (
                    <div 
                      key={country.code}
                      className={`flex items-center justify-between p-4 bg-slate-50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}
                      data-testid={`country-row-${country.code}`}
                    >
                      <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-sm font-mono font-bold text-primary">
                          {country.code}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {country[`name_${language}`] || country.name_en}
                          </p>
                          <p className="text-sm text-slate-500">{country.name_en}</p>
                        </div>
                      </div>
                      <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Badge variant={country.is_active ? 'default' : 'secondary'}>
                          {country.is_active ? t('active') : t('inactive')}
                        </Badge>
                        <Switch
                          checked={country.is_active}
                          onCheckedChange={() => toggleCountryStatus(country.code, country.is_active)}
                          data-testid={`country-toggle-${country.code}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Admins Tab */}
          <TabsContent value="admins">
            <Card>
              <CardHeader className={`flex flex-row items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div>
                  <CardTitle>{t('admin_admins')}</CardTitle>
                  <CardDescription>Gérez les administrateurs par pays</CardDescription>
                </div>
                <Dialog open={showAddAdmin} onOpenChange={setShowAddAdmin}>
                  <DialogTrigger asChild>
                    <Button className="gap-2" data-testid="add-admin-btn">
                      <Plus className="w-4 h-4" />
                      Ajouter Admin
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Nouvel Administrateur Pays</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('first_name')}</Label>
                          <Input 
                            value={newAdmin.first_name}
                            onChange={(e) => setNewAdmin({ ...newAdmin, first_name: e.target.value })}
                            data-testid="admin-first-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('last_name')}</Label>
                          <Input 
                            value={newAdmin.last_name}
                            onChange={(e) => setNewAdmin({ ...newAdmin, last_name: e.target.value })}
                            data-testid="admin-last-name"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('email')}</Label>
                        <Input 
                          type="email"
                          value={newAdmin.email}
                          onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                          data-testid="admin-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('password')}</Label>
                        <Input 
                          type="password"
                          value={newAdmin.password}
                          onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                          data-testid="admin-password"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Pays</Label>
                        <select 
                          className="w-full p-2 border rounded-md"
                          value={newAdmin.country_code}
                          onChange={(e) => setNewAdmin({ ...newAdmin, country_code: e.target.value })}
                          data-testid="admin-country-select"
                        >
                          <option value="">Sélectionner un pays</option>
                          {countries.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country[`name_${language}`] || country.name_en}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAddAdmin(false)}>
                        {t('cancel')}
                      </Button>
                      <Button onClick={handleAddAdmin} disabled={!newAdmin.country_code} data-testid="confirm-add-admin">
                        {t('save')}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {admins.filter(a => a.role === 'country_admin').map((admin) => (
                    <div 
                      key={admin.id}
                      className={`flex items-center justify-between p-4 bg-slate-50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}
                      data-testid={`admin-row-${admin.id}`}
                    >
                      <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className="w-10 h-10 bg-secondary/10 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-secondary" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{admin.first_name} {admin.last_name}</p>
                          <p className="text-sm text-slate-500">{admin.email}</p>
                        </div>
                      </div>
                      <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Badge variant="outline">{admin.country_code}</Badge>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="gap-2"
                              onClick={() => openRightsDialog(admin)}
                              data-testid={`rights-btn-${admin.id}`}
                            >
                              <Settings className="w-4 h-4" />
                              {t('admin_rights')}
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Droits de {admin.first_name} {admin.last_name}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              {Object.entries({
                                can_manage_professionals: 'Gérer les professionnels',
                                can_manage_patients: 'Gérer les patients',
                                can_view_appointments: 'Voir les rendez-vous',
                                can_manage_appointments: 'Gérer les rendez-vous',
                                can_view_statistics: 'Voir les statistiques',
                                can_send_notifications: 'Envoyer des notifications'
                              }).map(([key, label]) => (
                                <div key={key} className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                                  <Label className="cursor-pointer">{label}</Label>
                                  <Checkbox
                                    checked={adminRights[key]}
                                    onCheckedChange={(checked) => setAdminRights({ ...adminRights, [key]: checked })}
                                    data-testid={`right-${key}`}
                                  />
                                </div>
                              ))}
                            </div>
                            <DialogFooter>
                              <Button onClick={handleUpdateAdminRights} data-testid="save-rights-btn">
                                {t('save')}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  ))}
                  {admins.filter(a => a.role === 'country_admin').length === 0 && (
                    <p className="text-center text-slate-500 py-8">Aucun administrateur pays créé</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Signups Tab */}
          <TabsContent value="signups">
            <div className="space-y-6">
              {/* Period Selector */}
              <div className="flex items-center gap-3">
                <Label>Période :</Label>
                {[7, 14, 30, 90].map(d => (
                  <Button
                    key={d}
                    variant={signupsPeriod === d ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setSignupsPeriod(d); fetchRecentSignups(d); }}
                    data-testid={`signups-period-${d}`}
                  >
                    {d}j
                  </Button>
                ))}
              </div>

              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="border-blue-200 bg-blue-50/50">
                  <CardContent className="p-6 text-center">
                    <Shield className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-blue-700">{recentSignups.total_professionals || 0}</p>
                    <p className="text-sm text-slate-600">Professionnels ({signupsPeriod}j)</p>
                  </CardContent>
                </Card>
                <Card className="border-emerald-200 bg-emerald-50/50">
                  <CardContent className="p-6 text-center">
                    <Users className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-emerald-700">{recentSignups.total_patients || 0}</p>
                    <p className="text-sm text-slate-600">Patients ({signupsPeriod}j)</p>
                  </CardContent>
                </Card>
              </div>

              {/* Professionals Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-600" />
                    Nouveaux Professionnels
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {recentSignups.professionals?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="signups-professionals-table">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="pb-2 font-medium text-slate-600">Nom</th>
                            <th className="pb-2 font-medium text-slate-600">Spécialité</th>
                            <th className="pb-2 font-medium text-slate-600">Pays</th>
                            <th className="pb-2 font-medium text-slate-600">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentSignups.professionals.map((pro, idx) => (
                            <tr key={idx} className="border-b last:border-0">
                              <td className="py-2 font-medium">{pro.name || pro.email}</td>
                              <td className="py-2">{pro.specialty}</td>
                              <td className="py-2"><Badge variant="outline">{pro.country}</Badge></td>
                              <td className="py-2 text-slate-500">{pro.date ? new Date(pro.date).toLocaleDateString('fr-FR') : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-center text-slate-400 py-6">Aucune inscription professionnelle sur cette période</p>
                  )}
                </CardContent>
              </Card>

              {/* Patients Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-emerald-600" />
                    Nouveaux Patients
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {recentSignups.patients?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="signups-patients-table">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="pb-2 font-medium text-slate-600">Nom</th>
                            <th className="pb-2 font-medium text-slate-600">Pays</th>
                            <th className="pb-2 font-medium text-slate-600">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentSignups.patients.map((pat, idx) => (
                            <tr key={idx} className="border-b last:border-0">
                              <td className="py-2 font-medium">{pat.name || pat.email}</td>
                              <td className="py-2"><Badge variant="outline">{pat.country}</Badge></td>
                              <td className="py-2 text-slate-500">{pat.date ? new Date(pat.date).toLocaleDateString('fr-FR') : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-center text-slate-400 py-6">Aucune inscription patient sur cette période</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>{t('admin_notifications')}</CardTitle>
                <CardDescription>Historique des notifications envoyées (mock)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <div 
                      key={notif.id}
                      className="p-4 bg-slate-50 rounded-lg"
                      data-testid={`notif-${notif.id}`}
                    >
                      <div className={`flex items-start justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div>
                          <p className="font-medium text-slate-900">{notif.subject}</p>
                          <p className="text-sm text-slate-500">{notif.recipient_email}</p>
                        </div>
                        <Badge variant={notif.status === 'sent_mock' ? 'secondary' : 'default'}>
                          {notif.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(notif.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                  {notifications.length === 0 && (
                    <p className="text-center text-slate-500 py-8">Aucune notification envoyée</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Beta Codes Tab */}
          <TabsContent value="beta-codes">
            <div className="space-y-6">
              {/* Beta Mode Toggle Card */}
              <Card className={betaModeEnabled ? 'border-green-300 bg-green-50/30' : 'border-slate-200'}>
                <CardContent className="p-6">
                  <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${betaModeEnabled ? 'bg-green-100' : 'bg-slate-100'}`}>
                        <Power className={`w-7 h-7 ${betaModeEnabled ? 'text-green-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">Mode Accès Privé</h3>
                        <p className="text-sm text-slate-500">
                          {betaModeEnabled 
                            ? '🔒 L\'application nécessite un code d\'accès' 
                            : '🔓 L\'application est accessible à tous'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={betaModeEnabled ? 'default' : 'secondary'} className="text-sm px-3 py-1">
                        {betaModeEnabled ? 'ACTIVÉ' : 'DÉSACTIVÉ'}
                      </Badge>
                      <Switch
                        checked={betaModeEnabled}
                        onCheckedChange={handleToggleBetaMode}
                        data-testid="beta-mode-toggle"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Code Generator Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="w-5 h-5 text-primary" />
                    Générateur de Codes
                  </CardTitle>
                  <CardDescription>Générez des codes d'accès avec différentes durées de validité</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* 1 Hour Code */}
                    <Card className="border-2 border-amber-200 hover:border-amber-400 transition-colors cursor-pointer">
                      <CardContent className="p-6 text-center">
                        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Clock className="w-8 h-8 text-amber-600" />
                        </div>
                        <h4 className="font-bold text-slate-900 text-lg mb-1">1 Heure</h4>
                        <p className="text-sm text-slate-500 mb-4">Test rapide</p>
                        <Button 
                          className="w-full bg-amber-500 hover:bg-amber-600" 
                          onClick={() => handleGenerateBetaCode('1h')}
                          disabled={generatingCode}
                          data-testid="generate-1h-code"
                        >
                          {generatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Générer'}
                        </Button>
                      </CardContent>
                    </Card>

                    {/* 24 Hour Code */}
                    <Card className="border-2 border-blue-200 hover:border-blue-400 transition-colors cursor-pointer">
                      <CardContent className="p-6 text-center">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Clock className="w-8 h-8 text-blue-600" />
                        </div>
                        <h4 className="font-bold text-slate-900 text-lg mb-1">24 Heures</h4>
                        <p className="text-sm text-slate-500 mb-4">Test journalier</p>
                        <Button 
                          className="w-full bg-blue-500 hover:bg-blue-600" 
                          onClick={() => handleGenerateBetaCode('24h')}
                          disabled={generatingCode}
                          data-testid="generate-24h-code"
                        >
                          {generatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Générer'}
                        </Button>
                      </CardContent>
                    </Card>

                    {/* 14 Days Code */}
                    <Card className="border-2 border-purple-200 hover:border-purple-400 transition-colors cursor-pointer">
                      <CardContent className="p-6 text-center">
                        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Clock className="w-8 h-8 text-purple-600" />
                        </div>
                        <h4 className="font-bold text-slate-900 text-lg mb-1">14 Jours</h4>
                        <p className="text-sm text-slate-500 mb-4">Beta testeur</p>
                        <Button 
                          className="w-full bg-purple-500 hover:bg-purple-600" 
                          onClick={() => handleGenerateBetaCode('14d')}
                          disabled={generatingCode}
                          data-testid="generate-14d-code"
                        >
                          {generatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Générer'}
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>

              {/* Beta Analytics Summary */}
              {betaAnalytics && (
                <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-indigo-600" />
                      Analytics des Codes Beta
                    </CardTitle>
                    <CardDescription>Suivi de l'utilisation des codes d'accès</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                        <p className="text-3xl font-bold text-indigo-600">{betaAnalytics.summary?.total_codes || 0}</p>
                        <p className="text-sm text-slate-500">Codes Total</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                        <p className="text-3xl font-bold text-green-600">{betaAnalytics.summary?.active_codes || 0}</p>
                        <p className="text-sm text-slate-500">Codes Actifs</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                        <p className="text-3xl font-bold text-blue-600">{betaAnalytics.summary?.total_uses || 0}</p>
                        <p className="text-sm text-slate-500">Utilisations</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                        <p className="text-3xl font-bold text-purple-600">
                          {betaAnalytics.summary?.daily_usage?.length > 0 
                            ? betaAnalytics.summary.daily_usage[betaAnalytics.summary.daily_usage.length - 1]?.count || 0
                            : 0}
                        </p>
                        <p className="text-sm text-slate-500">Aujourd'hui</p>
                      </div>
                    </div>
                    
                    {/* Daily Usage Chart */}
                    {betaAnalytics.summary?.daily_usage?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">Utilisations ces 7 derniers jours</p>
                        <div className="flex items-end gap-1 h-20 bg-white rounded-lg p-2">
                          {betaAnalytics.summary.daily_usage.map((day, idx) => (
                            <div 
                              key={idx}
                              className="flex-1 bg-indigo-500 rounded-t hover:bg-indigo-600 transition-colors"
                              style={{ 
                                height: `${Math.max(10, (day.count / Math.max(...betaAnalytics.summary.daily_usage.map(d => d.count))) * 100)}%` 
                              }}
                              title={`${day._id}: ${day.count} utilisations`}
                            />
                          ))}
                        </div>
                        <div className="flex justify-between text-xs text-slate-400 mt-1">
                          {betaAnalytics.summary.daily_usage.map((day, idx) => (
                            <span key={idx}>{day._id?.slice(5) || ''}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* ==================== TRIAL CODES SECTION ==================== */}
              <Card className="border-2 border-emerald-200 bg-emerald-50/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-emerald-600" />
                    Codes d'Essai 14 Jours
                  </CardTitle>
                  <CardDescription>Générez des codes d'essai pour vos amis et contacts</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Quick Generate */}
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      className="bg-emerald-500 hover:bg-emerald-600"
                      onClick={handleCreateTrialCode}
                      disabled={creatingTrialCode}
                      data-testid="generate-trial-code"
                    >
                      {creatingTrialCode ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                      Générer Code 14 Jours
                    </Button>
                    <Input 
                      placeholder="Note (optionnel): ex. Pour Marie"
                      className="max-w-xs"
                      value={trialCodeNote}
                      onChange={(e) => setTrialCodeNote(e.target.value)}
                    />
                  </div>
                  
                  {/* Trial Codes List */}
                  {trialCodes.length > 0 ? (
                    <div className="space-y-2 mt-4">
                      <p className="text-sm font-medium text-slate-700">Codes générés ({trialCodes.length})</p>
                      {trialCodes.map((code) => (
                        <div 
                          key={code.id}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            code.is_active ? 'bg-white border border-emerald-200' : 'bg-slate-100 opacity-60'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                              <Gift className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-mono font-bold text-slate-900">{code.code}</p>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-6 w-6 p-0"
                                  onClick={() => copyToClipboard(code.code)}
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                              </div>
                              <p className="text-xs text-slate-500">
                                {code.duration_days} jours • {code.current_uses}/{code.max_uses} utilisations
                                {code.note && ` • ${code.note}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleExtendTrialCode(code.id, 7)}
                              disabled={!code.is_active}
                            >
                              +7 jours
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500"
                              onClick={() => handleDeactivateTrialCode(code.id)}
                              disabled={!code.is_active}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-4">Aucun code d'essai généré</p>
                  )}
                </CardContent>
              </Card>

              {/* Existing Codes List */}
              <Card>
                <CardHeader>
                  <CardTitle>Codes Générés ({betaCodes.length})</CardTitle>
                  <CardDescription>Liste des codes d'accès générés</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {betaCodes.map((code) => {
                      const isExpired = new Date(code.expires_at) < new Date();
                      const durationLabel = code.duration_type === '1h' ? '1 Heure' : 
                                           code.duration_type === '24h' ? '24 Heures' : 
                                           code.duration_type === '14d' ? '14 Jours' : code.duration_type;
                      return (
                        <div 
                          key={code.id}
                          className={`flex items-center justify-between p-4 rounded-lg ${
                            isExpired ? 'bg-slate-100 opacity-60' :
                            code.is_active ? 'bg-green-50 border border-green-200' : 'bg-slate-100'
                          }`}
                          data-testid={`beta-code-${code.id}`}
                        >
                          <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                              code.duration_type === '1h' ? 'bg-amber-100' :
                              code.duration_type === '24h' ? 'bg-blue-100' : 'bg-purple-100'
                            }`}>
                              <Key className={`w-6 h-6 ${
                                code.duration_type === '1h' ? 'text-amber-600' :
                                code.duration_type === '24h' ? 'text-blue-600' : 'text-purple-600'
                              }`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-mono font-bold text-lg text-slate-900">{code.code}</p>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-6 w-6 p-0"
                                  onClick={() => copyToClipboard(code.code)}
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                              </div>
                              <p className="text-sm text-slate-500">
                                <Badge variant="outline" className="mr-2 text-xs">{durationLabel}</Badge>
                                {code.current_uses}/{code.max_uses} utilisations • 
                                {isExpired ? ' Expiré' : ` Expire le ${new Date(code.expires_at).toLocaleString()}`}
                              </p>
                              {code.note && <p className="text-xs text-slate-400">{code.note}</p>}
                            </div>
                          </div>
                          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Badge variant={isExpired ? 'destructive' : code.is_active ? 'default' : 'secondary'}>
                              {isExpired ? 'Expiré' : code.is_active ? 'Actif' : 'Inactif'}
                            </Badge>
                            {!isExpired && (
                              <Switch
                                checked={code.is_active}
                                onCheckedChange={() => handleToggleBetaCode(code.id)}
                                data-testid={`beta-toggle-${code.id}`}
                              />
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-destructive"
                              onClick={() => handleDeleteBetaCode(code.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                    {betaCodes.length === 0 && (
                      <div className="text-center py-12 text-slate-500">
                        <Key className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p>Aucun code généré</p>
                        <p className="text-sm">Utilisez les boutons ci-dessus pour créer des codes d'accès</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Friend Codes Tab */}
          <TabsContent value="friend-codes">
            <div className="space-y-6">
              {/* Statistics */}
              {friendCodeStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-slate-900">{friendCodeStats.total || 0}</p>
                      <p className="text-sm text-slate-500">Total</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-green-600">{friendCodeStats.by_status?.active || 0}</p>
                      <p className="text-sm text-slate-500">Actifs</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-blue-600">{friendCodeStats.by_status?.used || 0}</p>
                      <p className="text-sm text-slate-500">Utilisés</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-red-500">{friendCodeStats.by_status?.expired || 0}</p>
                      <p className="text-sm text-slate-500">Expirés</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Code Generator */}
              <Card className="border-2 border-emerald-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-emerald-600" />
                    Générer un Code Ami
                  </CardTitle>
                  <CardDescription>Choisissez la durée, le type et le destinataire</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Duration Selector */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Durée de validité</Label>
                    <div className="flex gap-3" data-testid="friend-code-duration-selector">
                      <button
                        type="button"
                        className={`flex-1 p-4 rounded-lg border-2 text-center transition-all ${
                          newCodeDuration === 15 
                            ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => setNewCodeDuration(15)}
                        data-testid="duration-15-btn"
                      >
                        <p className="text-2xl font-bold text-slate-900">15</p>
                        <p className="text-sm text-slate-500">jours</p>
                      </button>
                      <button
                        type="button"
                        className={`flex-1 p-4 rounded-lg border-2 text-center transition-all ${
                          newCodeDuration === 30 
                            ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => setNewCodeDuration(30)}
                        data-testid="duration-30-btn"
                      >
                        <p className="text-2xl font-bold text-slate-900">30</p>
                        <p className="text-sm text-slate-500">jours</p>
                      </button>
                    </div>
                  </div>

                  {/* Recipient & Note */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nom du destinataire</Label>
                      <Input
                        placeholder="Ex: Dr. Martin"
                        value={newCodeRecipient}
                        onChange={(e) => setNewCodeRecipient(e.target.value)}
                        data-testid="friend-code-recipient"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Note (optionnel)</Label>
                      <Input
                        placeholder="Ex: Ami cardiologue"
                        value={newCodeNote}
                        onChange={(e) => setNewCodeNote(e.target.value)}
                        data-testid="friend-code-note"
                      />
                    </div>
                  </div>

                  {/* Generate Buttons */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Button
                      className="bg-blue-600 hover:bg-blue-700 gap-2"
                      onClick={() => generateFriendCode('professional')}
                      disabled={generatingCode}
                      data-testid="generate-pro-friend-code"
                    >
                      {generatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                      Code Professionnel ({newCodeDuration}j)
                    </Button>
                    <Button
                      className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                      onClick={() => generateFriendCode('patient')}
                      disabled={generatingCode}
                      data-testid="generate-patient-friend-code"
                    >
                      {generatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                      Code Patient ({newCodeDuration}j)
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Friend Codes List */}
              <Card>
                <CardHeader>
                  <CardTitle>Codes Amis Générés ({friendCodes.length})</CardTitle>
                  <CardDescription>Liste des codes amis avec leur statut</CardDescription>
                </CardHeader>
                <CardContent>
                  {friendCodes.length > 0 ? (
                    <div className="space-y-3">
                      {friendCodes.map((fc) => (
                        <div
                          key={fc.id || fc.code}
                          className={`flex items-center justify-between p-4 rounded-lg ${
                            fc.status === 'active' ? 'bg-emerald-50 border border-emerald-200' :
                            fc.status === 'used' ? 'bg-blue-50 border border-blue-200' :
                            'bg-slate-100 opacity-60'
                          }`}
                          data-testid={`friend-code-${fc.code}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              fc.type === 'professional_friend' ? 'bg-blue-100' : 'bg-emerald-100'
                            }`}>
                              {fc.type === 'professional_friend'
                                ? <Shield className="w-5 h-5 text-blue-600" />
                                : <Users className="w-5 h-5 text-emerald-600" />
                              }
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-mono font-bold text-slate-900">{fc.code}</p>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(fc.code)}>
                                  <Copy className="w-3 h-3" />
                                </Button>
                              </div>
                              <p className="text-xs text-slate-500">
                                <Badge variant="outline" className="mr-1 text-xs">
                                  {fc.type === 'professional_friend' ? 'Pro' : 'Patient'}
                                </Badge>
                                {fc.validity_days}j
                                {fc.recipient_name && ` - ${fc.recipient_name}`}
                                {fc.note && ` (${fc.note})`}
                              </p>
                              <p className="text-xs text-slate-400">
                                Expire : {fc.expires_at ? new Date(fc.expires_at).toLocaleDateString() : '-'}
                                {fc.used_by_email && ` | Utilisé par : ${fc.used_by_email}`}
                              </p>
                            </div>
                          </div>
                          <Badge variant={
                            fc.status === 'active' ? 'default' :
                            fc.status === 'used' ? 'secondary' :
                            'destructive'
                          }>
                            {fc.status === 'active' ? 'Actif' :
                             fc.status === 'used' ? 'Utilisé' :
                             fc.status === 'expired' ? 'Expiré' : 'Révoqué'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-400 py-8">Aucun code ami généré</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Marketing Tab */}
          <TabsContent value="marketing">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-primary" />
                  Campagnes Réseaux Sociaux
                </CardTitle>
                <CardDescription>Activez ou désactivez les campagnes publicitaires par plateforme</CardDescription>
              </CardHeader>
              <CardContent>
                {marketingSettings && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { key: 'whatsapp', label: 'WhatsApp', icon: '📱', color: 'bg-green-500' },
                        { key: 'facebook', label: 'Facebook', icon: '📘', color: 'bg-blue-600' },
                        { key: 'instagram', label: 'Instagram', icon: '📸', color: 'bg-pink-500' },
                        { key: 'youtube', label: 'YouTube', icon: '🎬', color: 'bg-red-600' },
                        { key: 'tiktok', label: 'TikTok', icon: '🎵', color: 'bg-black' },
                        { key: 'twitter', label: 'Twitter/X', icon: '🐦', color: 'bg-sky-500' }
                      ].map((platform) => (
                        <div 
                          key={platform.key}
                          className={`flex items-center justify-between p-4 rounded-lg border ${
                            marketingSettings[`${platform.key}_enabled`] ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'
                          }`}
                          data-testid={`marketing-${platform.key}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 ${platform.color} rounded-lg flex items-center justify-center text-white text-lg`}>
                              {platform.icon}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{platform.label}</p>
                              <p className="text-sm text-slate-500">
                                {marketingSettings[`${platform.key}_enabled`] ? 'Campagne active' : 'Désactivé'}
                              </p>
                            </div>
                          </div>
                          <Switch
                            checked={marketingSettings[`${platform.key}_enabled`] || false}
                            onCheckedChange={() => handleToggleMarketing(platform.key)}
                            data-testid={`marketing-toggle-${platform.key}`}
                          />
                        </div>
                      ))}
                    </div>
                    
                    <Card className="mt-6">
                      <CardHeader>
                        <CardTitle className="text-lg">Message personnalisé</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-600 p-4 bg-slate-50 rounded-lg">
                          {marketingSettings.custom_message}
                        </p>
                        <p className="text-sm text-slate-400 mt-2">
                          Lien: {marketingSettings.app_link}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Statistics Tab */}
          <TabsContent value="statistics">
            <div className="space-y-6">
              {/* Connection Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5 text-primary" />
                    Statistiques de Connexion
                  </CardTitle>
                  <CardDescription>Visites et conversions par pays</CardDescription>
                </CardHeader>
                <CardContent>
                  {connectionStats && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="bg-blue-50 border-blue-200">
                          <CardContent className="p-6">
                            <p className="text-sm text-blue-600 font-medium">Total Visites</p>
                            <p className="text-3xl font-bold text-blue-900">{connectionStats.total_visits}</p>
                          </CardContent>
                        </Card>
                        <Card className="bg-green-50 border-green-200">
                          <CardContent className="p-6">
                            <p className="text-sm text-green-600 font-medium">Conversions</p>
                            <p className="text-3xl font-bold text-green-900">{connectionStats.total_conversions}</p>
                          </CardContent>
                        </Card>
                        <Card className="bg-purple-50 border-purple-200">
                          <CardContent className="p-6">
                            <p className="text-sm text-purple-600 font-medium">Taux de Conversion</p>
                            <p className="text-3xl font-bold text-purple-900">{connectionStats.overall_conversion_rate}%</p>
                          </CardContent>
                        </Card>
                      </div>
                      
                      {/* By Country */}
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3">Par Pays</h4>
                        <div className="space-y-2">
                          {Object.entries(connectionStats.by_country || {}).map(([country, data]) => (
                            <div 
                              key={country}
                              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                            >
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{country}</span>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="text-sm text-slate-500">{data.total_visits} visites</span>
                                <span className="text-sm text-green-600">{data.conversions} conv.</span>
                                <Badge variant="outline">{data.rate}%</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Revenue Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    Revenus & Conversions Temps Réel
                  </CardTitle>
                  <CardDescription>Revenus, conversions Free → Premium et codes amis</CardDescription>
                </CardHeader>
                <CardContent>
                  {revenueStats && (
                    <div className="space-y-6">
                      {/* Revenue Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="bg-green-50 border-green-200">
                          <CardContent className="p-6">
                            <p className="text-sm text-green-600 font-medium">Revenus Mensuels</p>
                            <p className="text-3xl font-bold text-green-900">{revenueStats.real_revenue?.total?.toFixed(2) || '0.00'}€</p>
                            <div className="mt-2 text-xs text-green-600 space-y-0.5">
                              <p>Patients: {revenueStats.real_revenue?.patient?.toFixed(2) || 0}€</p>
                              <p>Pros: {revenueStats.real_revenue?.professional?.toFixed(2) || 0}€</p>
                              <p>Contrats: {revenueStats.real_revenue?.contracts?.toFixed(2) || 0}€</p>
                            </div>
                          </CardContent>
                        </Card>
                        <Card className="bg-blue-50 border-blue-200">
                          <CardContent className="p-6">
                            <p className="text-sm text-blue-600 font-medium">Revenus Potentiels</p>
                            <p className="text-3xl font-bold text-blue-900">{revenueStats.potential_revenue?.total?.toFixed(2) || '0.00'}€</p>
                            <p className="text-xs text-blue-500 mt-2">Si 100% des utilisateurs convertis</p>
                          </CardContent>
                        </Card>
                        <Card className="bg-amber-50 border-amber-200">
                          <CardContent className="p-6">
                            <p className="text-sm text-amber-600 font-medium">Abonnés Premium</p>
                            <p className="text-3xl font-bold text-amber-900">{(revenueStats.subscribers?.patient_premium || 0) + (revenueStats.subscribers?.professional_premium || 0)}</p>
                            <div className="mt-2 text-xs text-amber-600 space-y-0.5">
                              <p>Patients: {revenueStats.subscribers?.patient_premium || 0}</p>
                              <p>Pros: {revenueStats.subscribers?.professional_premium || 0}</p>
                              <p>Contrats: {revenueStats.subscribers?.active_contracts || 0}</p>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Conversion Funnels */}
                      {revenueStats.conversions && (
                        <div>
                          <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4" />
                            Entonnoir de Conversion
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Patient Funnel */}
                            <Card className="border-emerald-200" data-testid="patient-funnel">
                              <CardContent className="p-4">
                                <h5 className="font-medium text-slate-800 mb-3">Patients</h5>
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-slate-500">Free</span>
                                    <span className="font-bold">{revenueStats.conversions.patients?.free || 0}</span>
                                  </div>
                                  <div className="w-full bg-slate-100 rounded-full h-2">
                                    <div className="bg-slate-400 h-2 rounded-full" style={{width: '100%'}}></div>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-amber-600">En essai</span>
                                    <span className="font-bold text-amber-600">{revenueStats.conversions.patients?.trial || 0}</span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-emerald-600">Premium</span>
                                    <span className="font-bold text-emerald-600">{revenueStats.conversions.patients?.premium || 0}</span>
                                  </div>
                                  <div className="w-full bg-slate-100 rounded-full h-2">
                                    <div className="bg-emerald-500 h-2 rounded-full" style={{width: `${Math.max(revenueStats.conversions.patients?.rate || 0, 2)}%`}}></div>
                                  </div>
                                  <p className="text-right text-sm font-bold text-emerald-600">{revenueStats.conversions.patients?.rate || 0}% convertis</p>
                                </div>
                              </CardContent>
                            </Card>
                            {/* Professional Funnel */}
                            <Card className="border-blue-200" data-testid="pro-funnel">
                              <CardContent className="p-4">
                                <h5 className="font-medium text-slate-800 mb-3">Professionnels</h5>
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-slate-500">Free</span>
                                    <span className="font-bold">{revenueStats.conversions.professionals?.free || 0}</span>
                                  </div>
                                  <div className="w-full bg-slate-100 rounded-full h-2">
                                    <div className="bg-slate-400 h-2 rounded-full" style={{width: '100%'}}></div>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-amber-600">En essai</span>
                                    <span className="font-bold text-amber-600">{revenueStats.conversions.professionals?.trial || 0}</span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-blue-600">Premium</span>
                                    <span className="font-bold text-blue-600">{revenueStats.conversions.professionals?.premium || 0}</span>
                                  </div>
                                  <div className="w-full bg-slate-100 rounded-full h-2">
                                    <div className="bg-blue-500 h-2 rounded-full" style={{width: `${Math.max(revenueStats.conversions.professionals?.rate || 0, 2)}%`}}></div>
                                  </div>
                                  <p className="text-right text-sm font-bold text-blue-600">{revenueStats.conversions.professionals?.rate || 0}% convertis</p>
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                          {/* Friend Codes */}
                          {revenueStats.conversions.friend_codes && (
                            <div className="mt-3 flex gap-4 text-sm text-slate-500">
                              <span>Codes Amis utilisés : <strong className="text-slate-700">{revenueStats.conversions.friend_codes.used || 0}</strong></span>
                              <span>Codes actifs : <strong className="text-slate-700">{revenueStats.conversions.friend_codes.active || 0}</strong></span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Contracts Tab */}
          <TabsContent value="contracts">
            <Card>
              <CardHeader className={`flex flex-row items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileSignature className="w-5 h-5 text-primary" />
                    Contrats Professionnels
                  </CardTitle>
                  <CardDescription>Gérez les contrats d'abonnement des professionnels (SignRequest - MOCK)</CardDescription>
                </div>
                <Dialog open={showCreateContract} onOpenChange={setShowCreateContract}>
                  <DialogTrigger asChild>
                    <Button className="gap-2" data-testid="create-contract-btn">
                      <Plus className="w-4 h-4" />
                      Créer un contrat
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Créer un contrat d'abonnement</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Professionnel</Label>
                        <select 
                          className="w-full p-2 border rounded-md"
                          value={selectedProfessional}
                          onChange={(e) => setSelectedProfessional(e.target.value)}
                          data-testid="contract-pro-select"
                        >
                          <option value="">Sélectionner un professionnel</option>
                          {professionals.map((pro) => (
                            <option key={pro.id} value={pro.id}>
                              Dr. {pro.first_name} {pro.last_name} - {pro.email}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">
                          <strong>Type:</strong> Abonnement Premium Professionnel<br />
                          <strong>Prix:</strong> 150€/mois<br />
                          <strong>Service de signature:</strong> SignRequest (Mock)
                        </p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowCreateContract(false)}>
                        {t('cancel')}
                      </Button>
                      <Button onClick={handleCreateContract} disabled={!selectedProfessional} data-testid="confirm-create-contract">
                        Envoyer pour signature
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {contracts.map((contract) => (
                    <div 
                      key={contract.id}
                      className={`flex items-center justify-between p-4 rounded-lg ${
                        contract.status === 'signed' ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'
                      }`}
                      data-testid={`contract-${contract.id}`}
                    >
                      <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          contract.status === 'signed' ? 'bg-green-100' : 'bg-amber-100'
                        }`}>
                          <FileSignature className={`w-6 h-6 ${
                            contract.status === 'signed' ? 'text-green-600' : 'text-amber-600'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">Contrat #{contract.id.substring(0, 8)}</p>
                          <p className="text-sm text-slate-500">
                            {contract.monthly_price}€/mois • Créé le {new Date(contract.created_at).toLocaleDateString()}
                          </p>
                          {contract.signed_at && (
                            <p className="text-xs text-green-600">Signé le {new Date(contract.signed_at).toLocaleDateString()}</p>
                          )}
                        </div>
                      </div>
                      <Badge variant={contract.status === 'signed' ? 'default' : 'secondary'}>
                        {contract.status === 'signed' ? '✅ Signé' : '⏳ En attente'}
                      </Badge>
                    </div>
                  ))}
                  {contracts.length === 0 && (
                    <p className="text-center text-slate-500 py-8">Aucun contrat créé</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Deputies Tab */}
          <TabsContent value="deputies">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Admins Suppléants</CardTitle>
                  <CardDescription>Maximum 3 admins avec droits personnalisables</CardDescription>
                </div>
                <Dialog open={showAddDeputy} onOpenChange={setShowAddDeputy}>
                  <DialogTrigger asChild>
                    <Button className="gap-2" disabled={deputies.length >= 3} data-testid="add-deputy-btn">
                      <Plus className="w-4 h-4" />
                      Ajouter ({deputies.length}/3)
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Nouvel Admin Suppléant</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Prénom</Label>
                          <Input
                            value={newDeputy.first_name}
                            onChange={(e) => setNewDeputy({...newDeputy, first_name: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Nom</Label>
                          <Input
                            value={newDeputy.last_name}
                            onChange={(e) => setNewDeputy({...newDeputy, last_name: e.target.value})}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={newDeputy.email}
                          onChange={(e) => setNewDeputy({...newDeputy, email: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Mot de passe *</Label>
                        <Input
                          type="password"
                          value={newDeputy.password}
                          onChange={(e) => setNewDeputy({...newDeputy, password: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label className="mb-2 block">Droits (0 par défaut)</Label>
                        <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-lg">
                          {Object.entries(newDeputy.rights).map(([key, value]) => (
                            <div key={key} className="flex items-center gap-2">
                              <Checkbox
                                checked={value}
                                onCheckedChange={(checked) => setNewDeputy({
                                  ...newDeputy,
                                  rights: { ...newDeputy.rights, [key]: checked }
                                })}
                              />
                              <Label className="text-sm">{key.replace(/_/g, ' ')}</Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAddDeputy(false)}>Annuler</Button>
                      <Button onClick={handleAddDeputy}>Créer</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {deputies.map((deputy) => (
                    <div key={deputy.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-semibold">{deputy.first_name} {deputy.last_name}</p>
                          <p className="text-sm text-slate-500">{deputy.email}</p>
                        </div>
                        <Button variant="destructive" size="sm" onClick={() => handleDeleteDeputy(deputy.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {Object.entries(deputy.admin_rights || {}).map(([key, value]) => (
                          <div key={key} className="flex items-center gap-2">
                            <Checkbox
                              checked={value}
                              onCheckedChange={(checked) => handleUpdateDeputyRights(deputy.id, {
                                ...deputy.admin_rights,
                                [key]: checked
                              })}
                            />
                            <Label className="text-xs">{key.replace(/_/g, ' ')}</Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {deputies.length === 0 && (
                    <p className="text-center text-slate-500 py-8">Aucun admin suppléant</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Maintenance Tab */}
          <TabsContent value="maintenance">
            <Card>
              <CardHeader>
                <CardTitle>Mode Maintenance</CardTitle>
                <CardDescription>Arrêtez la plateforme globalement ou par pays</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Global Maintenance */}
                <div className="p-4 border-2 border-red-200 rounded-lg bg-red-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-red-800">Maintenance Globale</h3>
                      <p className="text-sm text-red-600">Arrête TOUTE la plateforme</p>
                    </div>
                    <Switch
                      checked={maintenance.global?.enabled || false}
                      onCheckedChange={(checked) => handleSetMaintenance(checked, null)}
                    />
                  </div>
                </div>

                {/* Per Country Maintenance */}
                <div>
                  <h3 className="font-semibold mb-3">Maintenance par Pays</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {countries.map((country) => {
                      const countryMaintenance = maintenance.per_country?.find(m => m.country_code === country.code);
                      return (
                        <div key={country.code} className="flex items-center justify-between p-3 border rounded-lg">
                          <span className="text-sm">{country.name_fr || country.code}</span>
                          <Switch
                            checked={countryMaintenance?.enabled || false}
                            onCheckedChange={(checked) => handleSetMaintenance(checked, country.code)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* System Health */}
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold">Santé du Système</h3>
                    <Button variant="outline" onClick={handleSystemRepair}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Auto-réparation
                    </Button>
                  </div>
                  {systemHealth && (
                    <pre className="text-xs bg-slate-100 p-2 rounded overflow-auto">
                      {JSON.stringify(systemHealth, null, 2)}
                    </pre>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing">
            <Card>
              <CardHeader>
                <CardTitle>Gestion des Prix</CardTitle>
                <CardDescription>Modifiez les tarifs des abonnements</CardDescription>
              </CardHeader>
              <CardContent>
                {pricing && (
                  <div className="grid gap-6 md:grid-cols-3">
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Users className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold">Patient Premium</p>
                          <p className="text-xs text-slate-500">{pricing.trial_days} jours d'essai</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          step="0.01"
                          defaultValue={pricing.patient_monthly}
                          className="w-24"
                          onBlur={(e) => handleUpdatePricing('patient_monthly', e.target.value)}
                        />
                        <span className="text-slate-500">€/mois</span>
                      </div>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <Shield className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-semibold">Pro Premium</p>
                          <p className="text-xs text-slate-500">Sans contrat</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          step="0.01"
                          defaultValue={pricing.professional_monthly}
                          className="w-24"
                          onBlur={(e) => handleUpdatePricing('professional_monthly', e.target.value)}
                        />
                        <span className="text-slate-500">€/mois</span>
                      </div>
                    </div>

                    <div className="p-4 border rounded-lg border-amber-300 bg-amber-50">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                          <FileSignature className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-semibold">Contrat Pro</p>
                          <p className="text-xs text-amber-600">Engagement 12 mois</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          step="0.01"
                          defaultValue={pricing.professional_contract}
                          className="w-24"
                          onBlur={(e) => handleUpdatePricing('professional_contract', e.target.value)}
                        />
                        <span className="text-slate-500">€/mois</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Durée d'essai gratuit</h4>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      defaultValue={pricing?.trial_days || 14}
                      className="w-20"
                      onBlur={(e) => handleUpdatePricing('trial_days', e.target.value)}
                    />
                    <span className="text-slate-500">jours</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ads Management Tab */}
          <TabsContent value="ads">
            <AdManagement token={token} />
          </TabsContent>

          {/* Ad Contracts Tab */}
          <TabsContent value="ad-contracts">
            <AdContractManager token={token} />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AnalyticsDashboard token={token} />
          </TabsContent>

          {/* Automation Tab */}
          <TabsContent value="automation">
            <AutomationPanel token={token} />
          </TabsContent>

          {/* Telephony Tab */}
          <TabsContent value="telephony">
            <div className="space-y-6">
              {/* Voice Assistant Selector */}
              <Card className="border-2 border-indigo-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="w-5 h-5 text-indigo-600" />
                    Assistant Vocal - Choix du moteur
                  </CardTitle>
                  <CardDescription>
                    Basculez entre ElevenLabs (clonage voix) et Azure/Vibe Voice (lecture chiffres précise)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <AssistantSelector token={token} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PhoneForwarded className="w-5 h-5" />
                    Transfert d'Appel
                  </CardTitle>
                  <CardDescription>
                    Configurez le transfert d'appel de votre mobile français vers l'assistant vocal IA
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Toggle ON/OFF */}
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50">
                    <div className="space-y-1">
                      <Label className="text-base font-medium">Transfert d'appel actif</Label>
                      <p className="text-sm text-muted-foreground">
                        Quand activé, les appels vers votre mobile seront transférés vers l'IA
                      </p>
                    </div>
                    <Switch
                      checked={callForwardingConfig.is_active}
                      onCheckedChange={async (checked) => {
                        setSavingCallForwarding(true);
                        try {
                          await axios.put(`${API_URL}/api/admin/call-forwarding`, 
                            { is_active: checked },
                            { headers: { Authorization: `Bearer ${token}` }}
                          );
                          setCallForwardingConfig(prev => ({ ...prev, is_active: checked }));
                          toast.success(checked ? 'Transfert activé' : 'Transfert désactivé');
                        } catch (error) {
                          toast.error('Erreur lors de la modification');
                        }
                        setSavingCallForwarding(false);
                      }}
                      disabled={savingCallForwarding}
                      data-testid="call-forwarding-toggle"
                    />
                  </div>

                  {/* Numbers Info */}
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="p-4 border rounded-lg">
                      <Label className="text-sm text-muted-foreground">Mobile Français (source)</Label>
                      <p className="text-lg font-mono font-medium">{callForwardingConfig.french_mobile}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Les patients appellent ce numéro
                      </p>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <Label className="text-sm text-muted-foreground">Numéro Vapi US (destination)</Label>
                      <p className="text-lg font-mono font-medium">{callForwardingConfig.us_vapi_number}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        L'appel est transféré vers l'assistant IA
                      </p>
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h4 className="font-medium text-amber-800 mb-2">⚠️ Configuration requise sur votre mobile</h4>
                    <p className="text-sm text-amber-700 mb-2">
                      Pour activer le transfert, vous devez configurer votre opérateur mobile :
                    </p>
                    <ol className="text-sm text-amber-700 list-decimal list-inside space-y-1">
                      <li>Composez <code className="bg-amber-100 px-1 rounded">**21*+12138452813#</code> pour activer</li>
                      <li>Composez <code className="bg-amber-100 px-1 rounded">##21#</code> pour désactiver</li>
                    </ol>
                  </div>

                  {/* Status Badge */}
                  <div className="flex items-center gap-2">
                    <Badge variant={callForwardingConfig.is_active ? "default" : "secondary"}>
                      {callForwardingConfig.is_active ? "🟢 Transfert ACTIF" : "⚪ Transfert INACTIF"}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Voice Assistant Numbers */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    Numéros Assistant Vocal
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">🇺🇸 États-Unis (Twilio - Trial)</p>
                        <p className="font-mono text-sm">+1 213 845 2813</p>
                      </div>
                      <Badge variant="outline">Actif</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">🇱🇺 Luxembourg (Telnyx)</p>
                        <p className="font-mono text-sm">+352 20 420 236</p>
                      </div>
                      <Badge variant="secondary">En attente validation</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-4">
                    Pour ajouter un numéro français ou belge, rendez-vous sur{' '}
                    <a href="https://dashboard.vapi.ai/phone-numbers" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                      dashboard.vapi.ai
                    </a>
                  </p>
                </CardContent>
              </Card>

              {/* Call Transfer Statistics */}
              <CallTransferStats token={token} />
            </div>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <div className="space-y-6">
              {/* Emergency Actions */}
              <Card className="border-red-200 bg-red-50/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    Actions d'Urgence
                  </CardTitle>
                  <CardDescription>
                    Contrôles critiques de la plateforme - À utiliser avec précaution
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Global Shutdown Button */}
                    <div className="p-4 border-2 border-red-300 rounded-lg bg-white">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-red-100 rounded-full">
                          <Power className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                          <h4 className="font-bold text-red-700">Arrêt Total Plateforme</h4>
                          <p className="text-sm text-gray-500">Arrête TOUTE la plateforme</p>
                        </div>
                      </div>
                      <Dialog open={showEmergencyShutdown} onOpenChange={setShowEmergencyShutdown}>
                        <DialogTrigger asChild>
                          <Button 
                            variant="destructive" 
                            className="w-full"
                            data-testid="emergency-shutdown-btn"
                          >
                            <ShieldOff className="w-4 h-4 mr-2" />
                            Arrêt d'Urgence
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-red-600">
                              <AlertTriangle className="w-5 h-5" />
                              Arrêt d'Urgence
                            </DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                              ⚠️ Cette action va mettre la plateforme en maintenance et empêcher toute connexion.
                            </div>
                            <div className="space-y-2">
                              <Label>Portée de l'arrêt</Label>
                              <div className="flex gap-4">
                                <label className="flex items-center gap-2">
                                  <input 
                                    type="radio" 
                                    value="global" 
                                    checked={shutdownScope === 'global'}
                                    onChange={(e) => setShutdownScope(e.target.value)}
                                  />
                                  <span>🌍 Global (tout)</span>
                                </label>
                                <label className="flex items-center gap-2">
                                  <input 
                                    type="radio" 
                                    value="country"
                                    checked={shutdownScope === 'country'}
                                    onChange={(e) => setShutdownScope(e.target.value)}
                                  />
                                  <span>🏳️ Un pays</span>
                                </label>
                              </div>
                            </div>
                            {shutdownScope === 'country' && (
                              <div className="space-y-2">
                                <Label>Pays</Label>
                                <select 
                                  className="w-full p-2 border rounded"
                                  value={shutdownCountry}
                                  onChange={(e) => setShutdownCountry(e.target.value)}
                                >
                                  <option value="">Sélectionner un pays</option>
                                  <option value="FR">🇫🇷 France</option>
                                  <option value="LU">🇱🇺 Luxembourg</option>
                                  <option value="MA">🇲🇦 Maroc</option>
                                  <option value="BE">🇧🇪 Belgique</option>
                                </select>
                              </div>
                            )}
                            <div className="space-y-2">
                              <Label>Raison de l'arrêt *</Label>
                              <Input 
                                value={shutdownReason}
                                onChange={(e) => setShutdownReason(e.target.value)}
                                placeholder="Ex: Maintenance urgente, problème de sécurité..."
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setShowEmergencyShutdown(false)}>
                              Annuler
                            </Button>
                            <Button 
                              variant="destructive"
                              onClick={async () => {
                                if (!shutdownReason) {
                                  toast.error('Veuillez indiquer une raison');
                                  return;
                                }
                                setProcessingShutdown(true);
                                try {
                                  const response = await axios.post(
                                    `${API_URL}/api/admin/security/emergency-shutdown`,
                                    {
                                      reason: shutdownReason,
                                      scope: shutdownScope,
                                      country_code: shutdownScope === 'country' ? shutdownCountry : null,
                                      estimated_duration_hours: 1
                                    },
                                    { headers }
                                  );
                                  toast.success('Arrêt d\'urgence activé');
                                  setShowEmergencyShutdown(false);
                                  setShutdownReason('');
                                  // Refresh data
                                  fetchSecurityData();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Erreur');
                                } finally {
                                  setProcessingShutdown(false);
                                }
                              }}
                              disabled={processingShutdown}
                            >
                              {processingShutdown ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer l\'arrêt'}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>

                    {/* Active Shutdowns */}
                    <div className="p-4 border rounded-lg bg-white">
                      <h4 className="font-bold mb-3 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-amber-500" />
                        Arrêts Actifs
                      </h4>
                      {activeShutdowns.length === 0 ? (
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle className="w-5 h-5" />
                          <span>Aucun arrêt en cours - Plateforme opérationnelle</span>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {activeShutdowns.map(shutdown => (
                            <div key={shutdown.id} className="flex items-center justify-between p-2 bg-red-50 border border-red-200 rounded">
                              <div>
                                <span className="font-medium text-red-700">
                                  {shutdown.scope === 'global' ? '🌍 Global' : `🏳️ ${shutdown.country_code}`}
                                </span>
                                <p className="text-xs text-gray-500">{shutdown.reason}</p>
                              </div>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={async () => {
                                  try {
                                    await axios.post(
                                      `${API_URL}/api/admin/security/end-shutdown`,
                                      { shutdown_id: shutdown.id },
                                      { headers }
                                    );
                                    toast.success('Plateforme restaurée');
                                    fetchSecurityData();
                                  } catch (error) {
                                    toast.error('Erreur');
                                  }
                                }}
                              >
                                Restaurer
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Security Metrics */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-100 rounded-full">
                        <Ban className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">IPs bloquées (24h)</p>
                        <p className="text-2xl font-bold">{securityDashboard?.metrics?.blocked_ips_24h || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-full">
                        <XCircle className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Connexions échouées (24h)</p>
                        <p className="text-2xl font-bold">{securityDashboard?.metrics?.failed_logins_24h || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-full">
                        <UserX className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Comptes suspendus</p>
                        <p className="text-2xl font-bold">{securityDashboard?.metrics?.suspended_users || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-100 rounded-full">
                        <DollarSign className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Paiements en retard</p>
                        <p className="text-2xl font-bold">{overduePayments.length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Overdue Payments / Bad Payers */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-orange-500" />
                    Gestion des Mauvais Payeurs
                  </CardTitle>
                  <CardDescription>
                    Utilisateurs avec paiements en retard. Après 3 relances, suspension automatique possible.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {overduePayments.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                      <p>Aucun paiement en retard</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {overduePayments.map(user => (
                        <div 
                          key={user.user_id}
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                        >
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                            <div className="flex gap-2 mt-1">
                              <Badge variant="outline">{user.role}</Badge>
                              <Badge variant="secondary">{user.days_overdue} jours de retard</Badge>
                              <Badge variant={user.reminders_sent >= 3 ? 'destructive' : 'secondary'}>
                                {user.reminders_sent}/3 relances
                              </Badge>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={user.reminders_sent >= 3}
                              onClick={async () => {
                                try {
                                  await axios.post(
                                    `${API_URL}/api/admin/security/send-payment-reminder`,
                                    { user_id: user.user_id },
                                    { headers }
                                  );
                                  toast.success('Relance envoyée');
                                  fetchSecurityData();
                                } catch (error) {
                                  toast.error('Erreur');
                                }
                              }}
                            >
                              <Bell className="w-4 h-4 mr-1" />
                              Relancer
                            </Button>
                            {user.should_suspend && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={async () => {
                                  if (!confirm(`Suspendre le compte de ${user.name} ?`)) return;
                                  try {
                                    await axios.post(
                                      `${API_URL}/api/admin/security/suspend-non-payer`,
                                      { user_id: user.user_id },
                                      { headers }
                                    );
                                    toast.success('Compte suspendu');
                                    fetchSecurityData();
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Erreur');
                                  }
                                }}
                              >
                                <UserX className="w-4 h-4 mr-1" />
                                Suspendre
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Refresh Button */}
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={fetchSecurityData}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualiser les données
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Suspension Tab */}
          <TabsContent value="suspension">
            <SuspensionManager token={token} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Ad Banner for Admin Preview */}
      <AdBanner position="bottom" />
    </div>
  );
};

export default SuperAdminDashboard;
